import { useCallback, useEffect, useState } from "react";
import type { SdkEventSnapshot } from "../types";

const fallback = (): SdkEventSnapshot => ({
  state: "waiting",
  updatedAt: null,
  message: "공식 SDK 연결 대기",
  generatedAt: new Date().toISOString(),
  events: [],
  terminals: [],
  flow: [],
});

const valid = (value: unknown): value is SdkEventSnapshot => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<SdkEventSnapshot>;
  return (
    ["connected", "delayed", "waiting", "error"].includes(candidate.state ?? "") &&
    typeof candidate.generatedAt === "string" &&
    Array.isArray(candidate.events) &&
    Array.isArray(candidate.terminals) &&
    Array.isArray(candidate.flow)
  );
};

export function useSdkEvents(enabled = true) {
  const [snapshot, setSnapshot] = useState<SdkEventSnapshot>(fallback);
  const [viewToken, setViewToken] = useState(() => window.sessionStorage.getItem("yurim-ops-view-token") ?? "");

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const response = await fetch("/api/sdk-events?limit=40", {
        headers: {
          accept: "application/json",
          ...(viewToken ? { "x-ops-view-token": viewToken } : {}),
        },
        cache: "no-store",
      });
      if (!response.ok) throw new Error(`sdk events api ${response.status}`);
      const payload: unknown = await response.json();
      setSnapshot(valid(payload) ? payload : fallback());
    } catch {
      setSnapshot(fallback());
    }
  }, [enabled, viewToken]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const timer = window.setInterval(() => void refresh(), 2_000);
    return () => window.clearInterval(timer);
  }, [enabled, refresh]);

  const unlock = (token: string) => {
    const normalized = token.trim();
    if (!normalized) return;
    window.sessionStorage.setItem("yurim-ops-view-token", normalized);
    setViewToken(normalized);
  };

  const lock = () => {
    window.sessionStorage.removeItem("yurim-ops-view-token");
    setViewToken("");
    setSnapshot(fallback());
  };

  return {
    ...snapshot,
    hasViewToken: Boolean(viewToken),
    unlock,
    lock,
  };
}
